/**
 * Polls a polling-transport run's event log to completion.
 *
 * discover PR #1859 lets an agent's response transport be "polling": the
 * completions POST returns `{run_id}` immediately instead of an SSE stream,
 * and the caller reads the turn's events off `GET .../runs/{run_id}/events`
 * instead. Each event's `data` field is EXACTLY the same `{code, message,
 * data}` envelope that would have arrived as one SSE `data:` line, so
 * `onEnvelope` is fed one envelope at a time and the caller (ChatScreen's
 * `processStreamingData`) needs no changes to consume either transport.
 *
 * Dependency-injected (`fetchImpl`, `sleepImpl`) so this is unit-testable
 * without real timers or a network, mirroring utils/progressPoller.js.
 */

// One second: matches the SSE-fallback progress poller's cadence
// (utils/progressPoller.js) — frequent enough that polling reads like a
// stream, without meaningfully multiplying requests over a turn's life.
export const POLL_INTERVAL_MS = 1000;
// Consecutive fetch failures (network errors, non-JSON bodies, non-zero
// `code`) before giving up on the run rather than polling a dead endpoint
// forever. Aligned with discover's own server-side LOST_AFTER_SECONDS=60
// (agent/run_store.py): giving up any sooner than the server would call the
// run "lost" means a brief client-side blip cancels a run that was never
// actually in trouble.
export const MAX_CONSECUTIVE_POLL_FAILURES = 60;

const TERMINAL_STATUSES = ['done', 'error', 'cancelled', 'lost'];

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Shaped like the `{code: 500, ...}` envelope the SSE path sends for a
// server-side failure, so processStreamingData renders it identically: it
// only ever reads `envelope.data.answer` / `.reference`, never `code`.
const syntheticError = (text) => ({
  code: 500,
  message: text,
  data: { answer: `**ERROR**: ${text}`, reference: [] },
});

/**
 * @param {object} opts
 * @param {string} opts.eventsUrl - the run's events endpoint, without query string
 * @param {() => object} opts.getHeaders - request headers (e.g. bearer auth), read fresh each poll
 * @param {(envelope: object) => void} opts.onEnvelope - called once per non-sentinel event, in order
 * @param {typeof fetch} [opts.fetchImpl]
 * @param {(ms: number) => Promise<void>} [opts.sleepImpl]
 * @returns {Promise<string>} the terminal status: 'done' | 'error' | 'cancelled' | 'lost' | 'connection_lost'
 */
export async function pollRunEvents({ eventsUrl, getHeaders, onEnvelope, fetchImpl = fetch, sleepImpl = sleep }) {
  let after = 0;
  let failures = 0;
  // Once we've relayed a real code-500 envelope, a terminal 'error' status is
  // already explained to the user — do not pile a second, generic error on top.
  let sawServerError = false;

  for (;;) {
    try {
      const res = await fetchImpl(`${eventsUrl}?after=${after}`, { headers: getHeaders() });
      const payload = await res.json();
      if (payload?.code !== 0) throw new Error(payload?.message || 'poll failed');
      failures = 0;

      const { events = [], next = after, status } = payload.data ?? {};
      for (const item of events) {
        const envelope = item?.data;
        if (!envelope || envelope.data === true) continue; // terminal sentinel: status below ends the loop instead
        if (envelope.code === 500) sawServerError = true;
        onEnvelope(envelope);
      }
      after = next;

      if (TERMINAL_STATUSES.includes(status)) {
        if (status === 'lost') {
          onEnvelope(syntheticError('The run was lost — the server stopped reporting progress.'));
        } else if (status === 'error' && !sawServerError) {
          onEnvelope(syntheticError('The agent run failed on the server.'));
        }
        return status;
      }
    } catch (e) {
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        onEnvelope(syntheticError('Lost connection while polling for the agent response.'));
        return 'connection_lost';
      }
    }
    await sleepImpl(POLL_INTERVAL_MS);
  }
}
